/**
 * Decoders for `Gradient` and `GradientTexture2D`, inline or from a `.tres`. Every
 * default is the value Godot's constructor installs (`scene/resources/gradient.cpp`,
 * `scene/resources/gradient_texture.cpp`): most resources omit most properties.
 * No THREE.
 */
import { enumOr, settableIntOr, vec2Or } from '../../../parser/valueParsers';
import { resolveReferencedResource } from '../../resolveReference';
import { GRADIENT_TEXTURE_MAX_SIZE, GradientTexture2DFill, GradientInterpolationMode, GradientTexture2DRepeat, } from '../../../godot/scene/gradientTexture.js';
import { boolSlotValue } from '../../../godot/variant/variantBool.js';
import { parsePackedColorArray, parsePackedFloat32Array } from '../../shapes/packedArray';
/**
 * Parses a `PackedColorArray` slot into a `Color[]`, dropping a trailing partial quadruple. It
 * groups the output of `parsePackedColorArray`, so each component is the float32 a `Color` stores
 * (`core/math/color.h:42-45`).
 */
function parseColorStops(value) {
    const nums = parsePackedColorArray(value);
    const colors = [];
    for (let i = 0; i + 3 < nums.length; i += 4) {
        colors.push({ r: nums[i], g: nums[i + 1], b: nums[i + 2], a: nums[i + 3] });
    }
    return colors;
}
/**
 * Pairs `offsets` with `colors` into sorted stops. Absent `offsets` spreads the
 * stops evenly across 0..1, the default two-point spacing. Mismatched lengths
 * use the shorter of the two.
 */
function decodeGradient(data) {
    const colors = data.colors ? safeColors(data.colors) : [];
    const offsets = data.offsets ? safeOffsets(data.offsets) : null;
    const count = offsets ? Math.min(offsets.length, colors.length) : colors.length;
    const stops = [];
    for (let i = 0; i < count; i++) {
        // A `Point` stores its offset as a float (`gradient.h:55`).
        const offset = Math.fround(offsets ? offsets[i] : count > 1 ? i / (count - 1) : 0);
        stops.push({ offset, color: colors[i] });
    }
    // Godot sorts points by offset before sampling. A stable sort keeps equal
    // offsets in authored order, as the binary search expects.
    stops.sort((a, b) => a.offset - b.offset);
    return {
        stops,
        interpolationMode: enumOr(data.interpolation_mode, GradientInterpolationMode.LINEAR, [GradientInterpolationMode.LINEAR, GradientInterpolationMode.CONSTANT, GradientInterpolationMode.CUBIC], 'Gradient.interpolation_mode'),
    };
}
/** The `Gradient` a resource holds, or null for no resource or one of another type. */
export function decodeGradientResource(resource) {
    return resource?.type === 'Gradient' ? decodeGradient(resource.data) : null;
}
/**
 * The `Gradient` that `ref` names: a SubResource, or an ExtResource once its `.tres` has loaded.
 * Null for no reference, an unknown id, a file not loaded yet, or a resource of another type.
 * The one path every reader of a Gradient reference takes: a texture, a noise texture, a particle ramp.
 */
export function resolveGradient(ref, pools, loadedFile) {
    return decodeGradientResource(resolveReferencedResource(ref, pools, loadedFile));
}
/** `width = 64` and `height = 64` (`gradient_texture.h:92-93`). */
const DEFAULT_TEXTURE_SIZE = 64;
/**
 * `set_width` and `set_height` refuse an axis outside 1 to `GRADIENT_TEXTURE_MAX_SIZE`
 * (`gradient_texture.cpp:324`, `:335`).
 */
const TEXTURE_SIZE_RANGE = { min: 1, max: GRADIENT_TEXTURE_MAX_SIZE };
/**
 * A `GradientTexture2D` sub-resource. The caller resolves the `gradient` ref.
 * Defaults: 64×64, linear fill, from (0,0) to (1,0), no repeat, LDR.
 */
export function decodeGradientTexture2D(data) {
    return {
        width: settableIntOr(data.width, DEFAULT_TEXTURE_SIZE, TEXTURE_SIZE_RANGE, 'GradientTexture2D.width'),
        height: settableIntOr(data.height, DEFAULT_TEXTURE_SIZE, TEXTURE_SIZE_RANGE, 'GradientTexture2D.height'),
        fill: enumOr(data.fill, GradientTexture2DFill.LINEAR, [GradientTexture2DFill.LINEAR, GradientTexture2DFill.RADIAL, GradientTexture2DFill.SQUARE], 'GradientTexture2D.fill'),
        fillFrom: vec2Or(data.fill_from, { x: 0, y: 0 }, 'GradientTexture2D.fill_from'),
        fillTo: vec2Or(data.fill_to, { x: 1, y: 0 }, 'GradientTexture2D.fill_to'),
        repeat: enumOr(data.repeat, GradientTexture2DRepeat.NONE, [GradientTexture2DRepeat.NONE, GradientTexture2DRepeat.REPEAT, GradientTexture2DRepeat.MIRROR], 'GradientTexture2D.repeat'),
        useHdr: boolSlotValue(data.use_hdr) === true,
    };
}
function safeColors(value) {
    try {
        return parseColorStops(value);
    }
    catch {
        return [];
    }
}
function safeOffsets(value) {
    try {
        return parsePackedFloat32Array(value);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=decode.js.map